import React, { useState, useRef, useEffect } from "react";
import { FaShoppingCart, FaTrash, FaCheck, FaPlus, FaMinus } from "react-icons/fa";
import { getAllProductsByCode } from "../../api/action/products";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createOrder, updateOrder } from "../../api/action/cart";
import { removeShoppingCartItem } from "../../api/action/cart";
import { academyLogger } from "utils/logger";

function POSDashboard() {
  const [cart, setCart] = useState([]);
  const [searchCode, setSearchCode] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetails, setProductDetails] = useState(null);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalAmount, setTotalAmount] = useState(0);
  const itemsPerPage = 10;
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [, setLoading] = useState(false);
  const userDetail = useSelector((state) => state?.auth?.data);
  const companyState = useSelector((state) => state.companyReducer.company);
  const companyOperatingLocation = companyState?.operatingLocation?.code;

  // Retrieve company location from localStorage and set currency symbol
  const companyLocation = companyOperatingLocation;
  const currencySymbol = companyLocation === "IND" ? "₹" : "$";

  // Add product to cart
  const addToCart = (product) => {
    const exists = cart.find((item) => item.primaryId === product.primaryId);
    if (exists) {
      setCart(cart.map((item) => item.primaryId === product.primaryId ? { ...item, qty: item.qty + 1 } : item));
    } else {
      setCart([...cart, { ...product, qty: 1 }]);
      setSelectedProduct(product.primaryId);
    }
    setProductDetails(product); // Show product details
  };

  // Increase quantity
  const increaseQuantity = (id) => {
    setCart(cart.map((item) => item.primaryId === id ? { ...item, qty: item.qty + 1 } : item));
  };

  // Decrease quantity
  const decreaseQuantity = (id) => {
    setCart(cart.map((item) => item.primaryId === id ? { ...item, qty: Math.max(1, item.qty - 1) } : item));
  };

  // Handle search for product
  const handleSearch = async () => {
    try {
      const products = await dispatch(getAllProductsByCode(searchCode));
      if (products.length > 0) {
        const product = products[0];
        addToCart(product);
        setSearchCode("");
        setError("");
      } else {
        setError("Product not found. Please check the code and try again.");
      }
    } catch (error) {
      academyLogger.error("Product not found:", error);
      setError("Product not found. Please check the code and try again.");
    }
  };

  // Handle Enter key in search input
  const handleKeyDown = (e) => {
    if (document.activeElement === inputRef.current && e.key === "Enter") {
      handleSearch();
    }
  };

  // Handle arrow keys for quantity adjustment
  const handleQuantityKeyDown = (e, id) => {
    if (selectedProduct === id) {
      if (e.key === "ArrowUp") {
        increaseQuantity(id);
      } else if (e.key === "ArrowDown") {
        decreaseQuantity(id);
      } else if (e.key === "Enter") {
        setSelectedProduct(null);
      }
    }
  };

  const selectedRowRef = useRef(null);

  useEffect(() => {
    if (selectedRowRef.current) {
      selectedRowRef.current.focus();
    }
  }, [selectedProduct]);

  const handleRowClick = (product) => {
    setSelectedProduct(product.primaryId);
    setProductDetails(product);
  };

  useEffect(() => {
    const newTotalAmount = cart.reduce((acc, item) => acc + item.price.priceAmt * item.qty, 0);
    setTotalAmount(newTotalAmount);
  }, [cart]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = cart.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleRetailOrder = async () => {
    const lineItemList = cart.map((item) => ({
      quantity: item.qty,
      totalPrice: item.price.priceAmt * item.qty,
      product: {
        code: item.product?.code,
        name: item.product?.name,
        primaryId: item.product?.primaryId,
        _id: item.product?._id,
      },
    }));
    addUpdateOrder(lineItemList);
  };

  const addUpdateOrder = async (lineItemList) => {
    const userDet = userDetail;

    const orderRequest = {
      _id: userDet.account._id,
      primaryId: userDet.account.primaryId,
      orderList: [
        {
          totalLineItems: cart.length,
          totalAmount: totalAmount,
          subtotalAmount: totalAmount,
          shippingAndHandlingAmount: 0,
          tax: 0,
          lineItemList,
        },
      ],
    };

    const existingOrder = JSON.parse(localStorage.getItem("orderDetails"));

    try {
      setLoading(true); // Start loading

      if (existingOrder === null) {
        const createdOrderRes = await createOrder(orderRequest);
        if (createdOrderRes) {
          localStorage.setItem("orderDetails", JSON.stringify(createdOrderRes));
          // localStorage.setItem("totalAmount", totalAmount);

          navigate("/order-history");
        }
      } else {
        const req = {
          ...existingOrder,
          totalLineItems: cart.length,
          totalAmount: totalAmount,
          subtotalAmount: totalAmount,
          shippingAndHandlingAmount: 0,
          tax: 0,
          lineItemList,
        };
        const updatedOrderRes = await updateOrder(req);
        if (updatedOrderRes) {
          localStorage.setItem("orderDetails", JSON.stringify(updatedOrderRes));
          // localStorage.setItem("totalAmount", totalAmount);

          navigate("/order-history");
        }
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const handleRemoveItem = async (index) => {
    const updatedCart = cart.filter((item) => item.primaryId !== index);
    setCart(updatedCart);
    removeItem(index, updatedCart);
  };

  const removeItem = async (index, updatedCart) => {
    const shoppingCartId = userDetail.account.shoppingCart.primaryId;
    try {
      const nextCart = updatedCart || cart.filter((item) => item.primaryId !== index);
      const data = await removeShoppingCartItem({
        primaryId: shoppingCartId,
        accountId: userDetail?.account?.primaryId,
        productList: nextCart,
        totalPrice: nextCart.reduce((initial, item) => initial + item.price.priceAmt * item.qty, 0),
        company: companyState || userDetail?.account?.company || {},
        modifiedBy: userDetail?.email || "POS"
      });
      if (data) {
        const newPrice = nextCart.reduce((initial, item) => initial + item.price.priceAmt * item.qty, 0);
        setTotalAmount(newPrice);
        setCart(nextCart);
      }
    } catch (error) {
      setError("Failed to remove item");
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen flex flex-col items-center" tabIndex={0} onKeyDown={handleKeyDown} style={{ marginTop: "70px" }}>
      {/* Header */}
      <div className="w-full bg-black text-white py-6 mb-6 shadow-lg border-b border-green-400">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-extrabold text-green-400 glow">Sales Dashboard</h1>
          <p className="text-md text-gray-300">Track real-time sales with advanced analytics</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl w-full flex gap-6">
        {/* Left Side: Search and Product Details */}
        <div className="w-1/3">
          {/* Search Section */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-bold mb-4">Search Product</h2>
            <div className="flex gap-4">
              <input
                ref={inputRef}
                type="text"
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter Product Code"
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value)}
              />
              <button className="bg-[#f4a649] text-white px-6 py-3 rounded-lg hover:bg-[#e3953d] transition duration-200 flex items-center" onClick={handleSearch}>
                <FaShoppingCart className="mr-1" /> Add
              </button>
            </div>
            {error && <p className="text-red-500 mt-2">{error}</p>}
          </div>

          {/* Product Details Section */}
          {productDetails && (
            <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
              {/* Product Image (Optimized Size) */}
              <div className="w-full md:w-1/2 flex justify-center">
                <img
                  src={productDetails.imageUrlsList?.[0]}
                  alt={productDetails.name}
                  className="w-[90%] md:w-full h-56 object-cover rounded-lg shadow-md border"
                />
              </div>

              {/* Product Info */}
              <div className="w-full md:w-1/2 flex flex-col justify-center space-y-4">
                {/* Product Name */}
                <h2 className="text-xl font-semibold text-gray-900 border-b pb-1 text-center md:text-left">
                  {productDetails.name}
                </h2>

                {/* Highlighted Category */}
                <div className="flex justify-center md:justify-start">
                  <p className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium inline-block">
                    {productDetails.category.name}
                  </p>
                </div>

                {/* Price */}
                <p className="text-lg font-semibold text-green-600 text-center md:text-left">
                  {currencySymbol}{productDetails.price.priceAmt.toFixed(2)}
                </p>

                {/* Description */}
                <p className="text-sm text-gray-700 leading-relaxed text-center md:text-left">
                  {productDetails.description.split(" ").slice(0, 10).join(" ")}...
                  <span className="tooltip" title={productDetails.description}>more</span>
                </p>

                {/* Add to Cart Button */}

              </div>
            </div>
          )}
        </div>

        {/* Right Side: Cart Section */}
        <div className="w-2/3">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <FaShoppingCart className="mr-2" /> Cart
            </h2>
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-200">
                  <th className="p-3 border border-gray-300">Si.No</th>
                  <th className="p-3 border border-gray-300">Product</th>
                  <th className="p-3 border border-gray-300">Category</th>
                  <th className="p-3 border border-gray-300">Qty</th>
                  <th className="p-3 border border-gray-300">Unit Price</th>
                  <th className="p-3 border border-gray-300">Total</th>
                  <th className="p-3 border border-gray-300">Action</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item, index) => (
                  <tr
                    key={item.primaryId}
                    className={`border-b hover:bg-gray-100 transition duration-200 ${selectedProduct === item.primaryId ? "bg-gray-200" : ""}`}
                    onClick={() => handleRowClick(item)}
                    tabIndex={0}
                    onKeyDown={(e) => handleQuantityKeyDown(e, item.primaryId)}
                    ref={selectedProduct === item.primaryId ? selectedRowRef : null}
                  >
                    <td className="p-3 border border-gray-300 text-center">{indexOfFirstItem + index + 1}</td>
                    <td className="p-3 border border-gray-300">{item.name}</td>
                    <td className="p-3 border border-gray-300">{item.category.name}</td>
                    <td className="p-3 border border-gray-300">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          className="p-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition duration-200"
                          onClick={() => decreaseQuantity(item.primaryId)}
                        >
                          <FaMinus />
                        </button>
                        <span>{item.qty}</span>
                        <button
                          className="p-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition duration-200"
                          onClick={() => increaseQuantity(item.primaryId)}
                        >
                          <FaPlus />
                        </button>
                      </div>
                    </td>
                    <td className="p-3 border border-gray-300 text-center">{currencySymbol}{item.price.priceAmt.toFixed(2)}</td>
                    <td className="p-3 border border-gray-300 text-center">{currencySymbol}{(item.price.priceAmt * item.qty).toFixed(2)}</td>
                    <td className="p-3 border border-gray-300 text-center">
                      <button
                        className="text-red-500 hover:text-red-700 transition duration-200"
                        onClick={() => handleRemoveItem(item.primaryId)}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-between items-center mt-4">
              <h3 className="text-xl font-bold">
                Overall Total: <span className="text-green-600">{currencySymbol}{totalAmount.toFixed(2)}</span>
              </h3>
              <button className="bg-[#f4a649] text-white px-6 py-3 rounded-lg hover:bg-[#e3953d] transition duration-200 flex items-center" onClick={handleRetailOrder}>
                <FaCheck className="mr-2" /> Checkout
              </button>
            </div>
            {/* Pagination */}
            <div className="flex justify-center mt-4">
              <nav>
                <ul className="flex list-none">
                  {Array.from({ length: Math.ceil(cart.length / itemsPerPage) }, (_, i) => (
                    <li key={i} className="mx-1">
                      <button
                        onClick={() => paginate(i + 1)}
                        className={`px-3 py-1 rounded-lg ${currentPage === i + 1 ? "bg-[#4ADE80] text-white" : "bg-gray-200 text-gray-700"}`}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default POSDashboard;
